import { FC, memo, ReactNode, useEffect, useState } from 'react'
import { useAppDispatch as useDispatch, useAppSelector as useSelector } from '../../../store'
import Roles from '../../../models/roles'
import { useLocation, useNavigate } from 'react-router'
import { NON_AUTH_ROUTE } from '../../../pages/App/routes'
import { resetUploadsErrorAction } from '../../../store/uploads/slice'
import { resetAuthErrorAction } from '../../../store/auth/slice'
import { resetOnboardingErrorAction } from '../../../store/onboarding/slice'

/**
 * Component Props Interface
 * @interface ComponentProps
 */
interface ComponentProps {}

/**
 * PrivateRouteWithLayout Props Interface
 * @interface PrivateRouteWithLayoutProps
 */
interface PrivateRouteWithLayoutProps {
  /** Loading Layout */
  layout: FC<any>
  /** Loading Component */
  component: FC<ComponentProps>
  /** Roles of Users */
  roles: Roles[]
  /** Page content */
  children?: ReactNode
}

const PrivateRouteWithLayout: FC<PrivateRouteWithLayoutProps> = ({
  component: Component,
  layout: Layout,
  children,
  roles
}) => {
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const location = useLocation()
  const [isLayoutReady, setLayoutReady] = useState(false)

  const user = useSelector((state) => state.auth.user)
  const authError = useSelector((state) => state.auth.error)
  const onboardingError = useSelector((state) => state.onboarding.error)
  const uploadsError = useSelector((state) => state.uploads.error)

  const token = localStorage.getItem('token')

  useEffect(() => {
    if (!user && (token === '' || token === null) && location.pathname !== '/reset-password') {
      navigate(NON_AUTH_ROUTE.LOGIN)
    }
  }, [user, token, navigate, location])

  useEffect(() => {
    if(
      ((authError !== null || authError !== '') && (authError?.message === "Token has been invalidated" || authError?.message === "Invalid token")) || 
      ((onboardingError !== null || onboardingError !== '') && (onboardingError?.message === "Token has been invalidated" || onboardingError?.message === "Invalid token")) || 
      ((uploadsError !== null || uploadsError !== '') && (uploadsError?.message === "Token has been invalidated" || uploadsError?.message === "Invalid token")) || 
      ((onboardingError !== null || onboardingError !== '') && onboardingError?.message === "Token has expired")
    ) {
      localStorage.clear()
      sessionStorage.clear()
      navigate(NON_AUTH_ROUTE.LOGIN)
      dispatch(resetAuthErrorAction());
      dispatch(resetOnboardingErrorAction());
      dispatch(resetUploadsErrorAction());
    }
  }, [authError, onboardingError, uploadsError, navigate, dispatch])

  return (user && roles.includes(user.role.name)) ? (
    <Layout setLayoutReady={setLayoutReady}>
      {isLayoutReady ? <Component>{children}</Component> : null}
    </Layout>
  ) : null
}

export default memo(PrivateRouteWithLayout)
